type LessonEntitlementCurriculumType = 'PODO_TRIAL' | 'PODO_BASIC' | 'PODO_BUSINESS' | string

type LessonEntitlementLanguageStatus = {
  langType?: string | null
  regClassCount?: number | null
  isUnlimitedTicket?: 'Y' | 'N' | string | null
  curriculumType?: LessonEntitlementCurriculumType | null
}

type FirstTrialOnboardingUserStatus = {
  paymentYn: 'Y' | 'N' | string
  trialPaymentYn: 'Y' | 'N' | string
  companyCode?: string | null
  regClassCountForLangTypeList?: readonly LessonEntitlementLanguageStatus[] | null
}

type FirstTrialPopupUserStatus = FirstTrialOnboardingUserStatus & {
  trialClassCompYn: 'Y' | 'N' | string
  countryCode?: string | null
  companyCode?: string | null
  remainTicketCount?: string | number | null
}

type FirstTrialOnboardingEntrySource = 'admin_force' | string
type OnboardingDraftStatus = 'idle' | 'in-progress' | 'completed' | string

export type OnboardingStateLookupStatus = 'loading' | 'error' | 'missing' | 'existing'
type JpTrialExperienceState = {
  state: string
  reason: string | null
}

const hasJpTrialHistory = (trialExperienceStates: readonly (JpTrialExperienceState | null | undefined)[]) =>
  trialExperienceStates.some((state) => state && !(state.state === 'UNKNOWN' && state.reason === 'NO_TRIAL_HISTORY'))

export type JpTrialHomeCtaVariant = 'book-trial' | 'level-test' | 'rebook'

/**
 * JP 홈의 무수강권 카드에 노출할 체험 CTA를 서버 체험 상태로 판정한다.
 * 체험 이력이 없거나 미예약 체험권이 있으면 최초 예약을, 취소/1회 불참이면 재예약을 유도한다.
 * 2회 불참은 신청 화면에서도 차단되므로 제외한다.
 */
export const resolveJpTrialHomeCtaVariant = ({
  countryCode,
  paymentYn,
  trialExperienceStates,
  hasUpcomingLesson,
  additionalTrialEligible = false,
}: {
  countryCode?: string | null
  paymentYn: string
  trialExperienceStates: readonly JpTrialExperienceState[]
  hasUpcomingLesson: boolean
  additionalTrialEligible?: boolean
}): JpTrialHomeCtaVariant | null => {
  if (countryCode?.trim().toUpperCase() !== 'JP' || paymentYn !== 'N' || hasUpcomingLesson) {
    return null
  }

  const hasRetryableHistory = trialExperienceStates.some(
    ({ state, reason }) => state === 'FIRST_NOSHOW' || reason === 'RESERVATION_CANCELED',
  )
  if (hasRetryableHistory) {
    return 'rebook'
  }

  if (trialExperienceStates.some(({ state }) => state === 'COMPLETED')) {
    return additionalTrialEligible ? 'rebook' : 'level-test'
  }

  const canBookFirstTrial = trialExperienceStates.some(
    ({ state, reason }) =>
      (state === 'UNKNOWN' && reason === 'NO_TRIAL_HISTORY') ||
      (state === 'NOT_RESERVED' && reason === 'TRIAL_TICKET_AVAILABLE'),
  )

  return canBookFirstTrial ? 'book-trial' : null
}

const REGULAR_LESSON_CURRICULUM_TYPES = new Set<LessonEntitlementCurriculumType>(['PODO_BASIC', 'PODO_BUSINESS'])

/**
 * 프로필 API(`/api/v1/user/podo/getInfo`)는 커리큘럼 타입을 접두사 없이 내려준다
 * (실제 값: TRIAL / BASIC / BUSINESS / SMART_TALK). 다른 API가 `PODO_` 접두사를 붙인
 * 형태를 쓰기도 하므로 양쪽을 같은 값으로 정규화한다.
 */
const normalizeCurriculumType = (curriculumType: LessonEntitlementCurriculumType) =>
  curriculumType
    .trim()
    .toUpperCase()
    .replace(/^PODO_/, '')

export const isKoreanMarketUser = (countryCode: string | null | undefined) => countryCode?.trim().toUpperCase() === 'KR'

/**
 * KR 경로 전용 정규권 판정. `PODO_` 접두사가 붙은 값만 인식하므로 프로필 API 의 실제 값
 * (`BASIC` 등)과는 맞지 않아 사실상 항상 false 다.
 *
 * 이 불일치를 여기서 고치면 KR 의 첫 체험 팝업·온보딩 유도·홈 CTA·레슨 시작 스타일 진입점이
 * 한꺼번에 바뀐다. 결제 없이 정규권을 지급받은 KR 유저가 실제로 수천 명 있어 영향이 크므로,
 * KR 동작 변경은 이 함수를 손대는 별도 작업으로 분리한다.
 * 접두사 없는 값까지 보려면 {@link hasAnyRegularLessonEntitlement} 를 쓴다.
 */
export const hasRegularLessonEntitlement = (userStatus: {
  regClassCountForLangTypeList?: readonly LessonEntitlementLanguageStatus[] | null
}) => {
  return Boolean(
    userStatus.regClassCountForLangTypeList?.some((item) => {
      if (!item.curriculumType || !REGULAR_LESSON_CURRICULUM_TYPES.has(item.curriculumType)) {
        return false
      }

      return item.isUnlimitedTicket === 'Y' || Number(item.regClassCount ?? 0) > 0
    }),
  )
}

/**
 * 접두사 유무와 무관하게 "체험이 아닌 커리큘럼"을 정규 레슨으로 보는 판정.
 * 화이트리스트로 두면 커리큘럼이 늘어날 때마다 정규권 보유자가 다시 새어나오므로,
 * TRIAL 만 제외하는 방향으로 뒤집었다. 현재는 JP 체험 신청 게이트에서만 쓴다.
 */
export const hasAnyRegularLessonEntitlement = (userStatus: {
  regClassCountForLangTypeList?: readonly LessonEntitlementLanguageStatus[] | null
}) => {
  return Boolean(
    userStatus.regClassCountForLangTypeList?.some((item) => {
      if (!item.curriculumType || normalizeCurriculumType(item.curriculumType) === 'TRIAL') {
        return false
      }

      return item.isUnlimitedTicket === 'Y' || Number(item.regClassCount ?? 0) > 0
    }),
  )
}

export const isFirstTrialOnboardingTarget = (userStatus: FirstTrialOnboardingUserStatus) => {
  return userStatus.paymentYn === 'N' && userStatus.trialPaymentYn === 'N' && !hasRegularLessonEntitlement(userStatus)
}

/**
 * JP 체험 신청 자격은 조회 시 생성되는 체험 티켓 때문에 결제 플래그만으로 판정할 수 없다.
 * 서버 체험 상태가 있으면 이를 단일 기준으로 사용하고, 상태 조회 실패 시에만 기존 신규 유저
 * 판정을 안전한 폴백으로 사용한다.
 *
 * 단 정규 수강권 보유자는 서버 체험 상태와 무관하게 제외한다. 체험 상태는 체험권·체험수업만
 * 보고 판정하므로(`TrialExperienceStateResolver`), 정규권만 지급받고 체험 이력이 없는 유저는
 * `UNKNOWN + NO_TRIAL_HISTORY`로 내려와 체험 신청 대상으로 잘못 잡힌다.
 */
export const canEnterJpTrialApplication = ({
  userStatus,
  trialExperienceState,
  hasAnyTrialHistory = false,
  additionalTrialEligible = false,
}: {
  userStatus: FirstTrialOnboardingUserStatus
  trialExperienceState: JpTrialExperienceState | null
  hasAnyTrialHistory?: boolean
  additionalTrialEligible?: boolean
}) => {
  if (hasCompanyCode(userStatus)) {
    return false
  }

  if (hasAnyRegularLessonEntitlement(userStatus)) {
    return false
  }

  if (!trialExperienceState) {
    return false
  }

  if (trialExperienceState.state === 'NOT_RESERVED' || trialExperienceState.state === 'FIRST_NOSHOW') {
    return true
  }

  if (trialExperienceState.state === 'COMPLETED') {
    return additionalTrialEligible
  }

  const hasNoTrialHistory =
    trialExperienceState.state === 'UNKNOWN' && trialExperienceState.reason === 'NO_TRIAL_HISTORY'

  return hasNoTrialHistory && (!hasAnyTrialHistory || additionalTrialEligible)
}

export const resolveJpTrialApplicationAvailableLanguages = ({
  userStatus,
  trialExperienceStates,
  additionalTrialEligible,
}: {
  userStatus: FirstTrialOnboardingUserStatus
  trialExperienceStates: {
    ko: JpTrialExperienceState | null | undefined
    en: JpTrialExperienceState | null | undefined
  }
  additionalTrialEligible: boolean
}) => {
  const hasAnyTrialHistory = hasJpTrialHistory(Object.values(trialExperienceStates))

  return (['ko', 'en'] as const).filter((language) => {
    const trialExperienceState = trialExperienceStates[language]
    return Boolean(
      trialExperienceState &&
      canEnterJpTrialApplication({
        userStatus,
        trialExperienceState,
        hasAnyTrialHistory,
        additionalTrialEligible,
      }),
    )
  })
}

export const shouldRedirectJpTrialApplicationFromHome = ({
  countryCode,
  userStatus,
  trialExperienceStates,
  hasUpcomingLesson,
  isCompletionReturn,
}: {
  countryCode?: string | null
  userStatus: FirstTrialOnboardingUserStatus
  trialExperienceStates: readonly JpTrialExperienceState[]
  hasUpcomingLesson: boolean
  isCompletionReturn: boolean
}) => {
  if (countryCode?.trim().toUpperCase() !== 'JP' || hasUpcomingLesson || isCompletionReturn) {
    return false
  }

  // 체험 신청 페이지 자동 진입은 최초 신청에만 적용한다.
  // 티켓 발급·취소·노쇼·완료 등 어떤 체험 이력이든 생긴 뒤에는 홈에 머무른다.
  if (hasJpTrialHistory(trialExperienceStates)) {
    return false
  }

  return trialExperienceStates.some((trialExperienceState) =>
    canEnterJpTrialApplication({ userStatus, trialExperienceState }),
  )
}

/**
 * 레슨 시작 스타일 진입점(홈/마이포도) 노출 자격.
 * 일반 상품 결제 이력이 있거나(paymentYn='Y'), 잔여 일반 수강권을 보유하면 노출한다.
 * 체험 전용 유저(둘 다 아님)에게는 진입점을 숨긴다.
 */
export const isLessonStartStyleEntryEligible = (userStatus: {
  paymentYn: 'Y' | 'N' | string
  regClassCountForLangTypeList?: readonly LessonEntitlementLanguageStatus[] | null
}) => {
  return userStatus.paymentYn === 'Y' || hasRegularLessonEntitlement(userStatus)
}

export const hasCompanyCode = (userStatus: { companyCode?: string | null }) => {
  return typeof userStatus.companyCode === 'string' && userStatus.companyCode.trim().length > 0
}

// 사용 가능한(기간 내·미소진) 수강권 잔여가 있는지. remainTicketCount는 체험 티켓도
// 포함하므로, 정규 수강권 활성 판정에는 paymentYn === 'Y' 전제와 함께 써야 한다.
export const hasRemainingLessonTicket = (userStatus: { remainTicketCount?: string | number | null }) => {
  return Number(userStatus.remainTicketCount ?? 0) > 0
}

export const canShowFirstTrialPopup = (userStatus: FirstTrialPopupUserStatus) => {
  return (
    isKoreanMarketUser(userStatus.countryCode) &&
    userStatus.trialClassCompYn === 'N' &&
    isFirstTrialOnboardingTarget(userStatus) &&
    !hasCompanyCode(userStatus)
  )
}

export type CtaToastState = 'before_trial' | 'in_trial' | 'trial_completed' | 'paid_expired'

// 홈 CTA 토스트 상태 판정 (podo-prd/john/gnb-cta-toast 부록 B).
// paymentYn 검사가 체험 플래그보다 앞: 체험 이력 없는 유료 만료 유저가
// 신청 불가능한 무료체험 CTA를 받지 않도록 한다.
export const getCtaToastState = (userStatus: FirstTrialPopupUserStatus | null | undefined): CtaToastState | null => {
  // 배럴로 export되는 공개 헬퍼라 유저 데이터 부재 시에도 크래시 없이 미노출로 안전 처리한다.
  if (!userStatus) {
    return null
  }

  if (!isKoreanMarketUser(userStatus.countryCode)) {
    return null
  }

  if (hasCompanyCode(userStatus)) {
    return null
  }

  if (hasRegularLessonEntitlement(userStatus)) {
    return null
  }

  if (userStatus.paymentYn === 'Y') {
    // 회차권 잔여가 남은 유료 유저는 아직 활성으로 간주해 미노출한다. hasRegularLessonEntitlement가
    // 무제한권·예약수업만 보고 회차권 잔여는 놓치는 공백을 여기서 보완한다(paymentYn === 'Y' 전제).
    if (hasRemainingLessonTicket(userStatus)) {
      return null
    }
    return 'paid_expired'
  }

  if (userStatus.trialPaymentYn === 'N') {
    return 'before_trial'
  }

  if (userStatus.trialClassCompYn === 'N') {
    return 'in_trial'
  }

  return 'trial_completed'
}

export const getHomeCtaToastState = ({
  userStatus,
  isJpTrialApplicationAvailable,
  isKrTrialApplicationAvailable,
}: {
  userStatus: FirstTrialPopupUserStatus | null | undefined
  isJpTrialApplicationAvailable: boolean
  isKrTrialApplicationAvailable: boolean
}): CtaToastState | null => {
  if (userStatus?.countryCode?.trim().toUpperCase() === 'JP') {
    return isJpTrialApplicationAvailable ? 'before_trial' : null
  }

  const state = getCtaToastState(userStatus)
  return state === 'before_trial' && !isKrTrialApplicationAvailable ? null : state
}

export const getOnboardingStateLookupStatus = ({
  isLoading,
  isError,
  state,
}: {
  isLoading: boolean
  isError: boolean
  state: unknown | null | undefined
}): OnboardingStateLookupStatus => {
  if (isLoading) {
    return 'loading'
  }

  if (isError) {
    return 'error'
  }

  return state === null ? 'missing' : 'existing'
}

export const canEnterFirstTrialOnboardingStart = ({
  userStatus,
  source,
  presetLanguage,
}: {
  userStatus: FirstTrialOnboardingUserStatus
  source?: FirstTrialOnboardingEntrySource
  presetLanguage?: unknown
}) => {
  if (hasCompanyCode(userStatus)) {
    return false
  }

  if (source === 'admin_force' || Boolean(presetLanguage)) {
    return true
  }

  return isFirstTrialOnboardingTarget(userStatus)
}

export const canRestoreFirstTrialOnboardingDraft = ({
  userStatus,
  isHomePath,
  onboardingDraftStatus,
}: {
  userStatus: FirstTrialOnboardingUserStatus
  isHomePath: boolean
  onboardingDraftStatus: OnboardingDraftStatus
}) => {
  return (
    isHomePath &&
    onboardingDraftStatus === 'in-progress' &&
    isFirstTrialOnboardingTarget(userStatus) &&
    !hasCompanyCode(userStatus)
  )
}

export const canRedirectHomeToFirstTrialOnboarding = ({
  userStatus,
  isHomePath,
  isOnboardingEligible,
  onboardingDraftStatus,
  onboardingStateStatus,
}: {
  userStatus: FirstTrialOnboardingUserStatus
  isHomePath: boolean
  isOnboardingEligible: boolean
  onboardingDraftStatus: OnboardingDraftStatus
  onboardingStateStatus: OnboardingStateLookupStatus
}) => {
  if (
    !isHomePath ||
    onboardingDraftStatus === 'completed' ||
    !isFirstTrialOnboardingTarget(userStatus) ||
    hasCompanyCode(userStatus)
  ) {
    return false
  }

  return isOnboardingEligible && onboardingStateStatus === 'missing'
}
